import { useEffect, useRef, useState } from "react";
import { sheetPdfFile, canvasRaster } from "./pdf";
import regular from "./fonts/Barlow-Regular.ttf";
import medium from "./fonts/Barlow-Medium.ttf";
import semibold from "./fonts/Barlow-SemiBold.ttf";

/**
 * True where the Print button should share a PDF instead of printing.
 *
 * `navigator.standalone` is set only by Safari on iPhone and iPad, and only
 * when the app was opened from the Home Screen — the one place window.print()
 * does nothing. Android, desktop and Safari in a tab keep the real Print.
 */
export function sharesPdf(nav = typeof navigator === "undefined" ? undefined : navigator) {
  return !!nav && nav.standalone === true && typeof nav.share === "function";
}

// Fetched URLs, not inlined: the build gives each font a hashed file under
// static/media, and the service worker keeps them out of its install-time
// download (see service-worker.js), so only the Home Screen app fetches them.
const FONT_URLS = { 400: regular, 500: medium, 600: semibold };

async function base64Of(url) {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`${url}: ${response.status}`);
  const bytes = new Uint8Array(await response.arrayBuffer());
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

/**
 * jsPDF and the three Barlow weights, loaded ahead of the tap. Building the
 * file is synchronous once these are here; fetching them is not, and iOS will
 * not open the share sheet after an await.
 */
export async function loadPdfKit() {
  const weights = Object.keys(FONT_URLS);
  const [{ jsPDF }, ...fonts] = await Promise.all([
    import(/* webpackChunkName: "pdf" */ "jspdf"),
    ...weights.map((w) => base64Of(FONT_URLS[w])),
  ]);
  return { jsPDF, fonts: Object.fromEntries(weights.map((w, i) => [w, fonts[i]])) };
}

const NOTICE_MS = 4000;

/**
 * The Print button's behaviour. Everywhere but an iPhone Home Screen app it
 * prints. There it builds the sheet as a PDF inside the tap and opens the
 * share sheet, whose Print prints it — or says why it could not, instead of
 * doing nothing, which is the bug this exists to fix.
 */
export function usePrintButton(paperProps, { nav, print = () => window.print(), loadKit = loadPdfKit } = {}) {
  const [shares] = useState(() => sharesPdf(nav));
  const [notice, setNotice] = useState(null);
  const kit = useRef(null);
  const failed = useRef(false);

  useEffect(() => {
    if (!shares) return undefined;
    let live = true;
    loadKit().then(
      (loaded) => {
        if (live) kit.current = loaded;
      },
      () => {
        if (live) failed.current = true;
      }
    );
    return () => {
      live = false;
    };
  }, [shares, loadKit]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  const onPrint = () => {
    if (!shares) {
      print();
      return;
    }
    if (!kit.current) {
      setNotice(
        failed.current
          ? "Couldn't load the PDF maker. Check the connection and reopen the app."
          : "Getting the PDF ready. Try again in a moment."
      );
      return;
    }
    const share = (nav || navigator).share.bind(nav || navigator);
    let file;
    try {
      file = sheetPdfFile(kit.current, paperProps, canvasRaster());
    } catch (e) {
      setNotice("Couldn't make the PDF.");
      return;
    }
    // No await before this call: iOS opens the sheet only inside the tap.
    share({ files: [file], title: file.name.replace(/\.pdf$/, "") }).catch((e) => {
      if (e && e.name === "AbortError") return; // closed the sheet: not an error
      setNotice("Couldn't open the share sheet.");
    });
  };

  return { sharesPdf: shares, onPrint, notice };
}
